<script>
import CartError from '@/components/atoms/CartError'
import ImageWrapper from '@/components/atoms/ImageWrapper'
import CartItem from '@/components/atoms/CartItem'
import cartHelper from '@/modules/mixins/cartHelper'
import cartWatcher from '@/modules/mixins/cartWatcher'
import Spinner from '@/components/atoms/Spinner'
import Deals from '@/components/atoms/restaurant/Deals'
import GoToCheckout from '@/components/atoms/restaurant/GoToCheckout'
import DeliveryDetails from '@/components/molecules/DeliveryDetails'
import CartPaymentDetails from '@/components/molecules/CartPaymentDetails'
import Recommender from '@/components/organism/Recommender'
import ModalInactiveItems from '@/components/organism/modal/ModalInactiveItems'
import Modal from '@/components/atoms/Modal.vue'
import AllowanceBudgetProgressbar from '@/components/molecules/AllowanceBudgetProgressbar.vue'

export default {
    name: 'CartBlock',
    components: {
        AllowanceBudgetProgressbar,
        Modal,
        CartPaymentDetails,
        DeliveryDetails,
        Deals,
        Spinner,
        CartItem,
        ImageWrapper,
        CartError,
        GoToCheckout,
        Recommender,
        ModalInactiveItems,
    },
    mixins: [cartHelper, cartWatcher],
    methods: {
        scrollToPlaceOrder() {
            document.querySelector('.btn-place-order').scrollIntoView({ behavior: 'smooth' })
        },
    },
}
</script>

<template lang="pug">
transition(name="toggle-cart")
    .card.cart-block.border-radius-md-md(v-if="lgDevice || (smDevice && cartOpen)" :class="longDelay ? 'cart-loading' : 'shadow-2'" id="cartBlock")
        .card-header.shadow-2.shadow-md-none.border-radius-top-md-md(:class="{'flex-wrap ': smDevice}")
            .grid.w-100(:class="lgDevice ? 'grid-cols-1' : 'grid-cols-3'")
                button.btn.btn-transparent.btn-icon.rounded-circle(v-if="smDevice" @click="hideCart")
                    icon(icon="fa-solid fa-angle-left")
                h3(v-if="isCheckout || smDevice" :class="{'mb-0 text-center': smDevice}")
                    template(v-if="smDevice") {{$t('order.basket')}}
                        small.d-block.text-73 {{restaurant.name || orderRestaurant.name}}
                    template(v-else) {{$t('order.your_order')}}
                div(v-if="isCheckout && !smDevice")
                  .btn.btn-primary.btn-block.btn-sm(@click="scrollToPlaceOrder")
                    svg(width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg")
                      path(d="M11.5312 1.53125L6.53125 6.53125C6.21875 6.84375 5.75 6.84375 5.46875 6.53125L0.46875 1.53125C0.15625 1.25 0.15625 0.78125 0.46875 0.5C0.75 0.1875 1.21875 0.1875 1.5 0.5L5.96875 4.96875L10.4688 0.46875C10.75 0.1875 11.2188 0.1875 11.5 0.46875C11.8125 0.78125 11.8125 1.25 11.5 1.53125H11.5312ZM11.5312 7.53125L6.53125 12.5312C6.21875 12.8438 5.75 12.8438 5.46875 12.5312L0.46875 7.53125C0.15625 7.25 0.15625 6.78125 0.46875 6.5C0.75 6.1875 1.21875 6.1875 1.5 6.5L5.96875 10.9688L10.4688 6.46875C10.75 6.1875 11.2188 6.1875 11.5 6.46875C11.8125 6.78125 11.8125 7.25 11.5 7.53125H11.5312Z" fill="white")
                    span {{ $t('order.scroll_to_place') }}
                go-to-checkout(
                    v-else-if="lgDevice"
                    :checkout-disabled="checkoutDisabled"
                    :go-to-checkout="goToCheckout"
                    :currency="currency"
                    :price="totalPrice"
                    :original-price="originalTotalPrice"
                    :minimum-order="minimumOrder"
                    :sub-total="subTotal"
                )
            template(v-if="isNotCheckoutPageAndHasAllowance")
              allowance-budget-progressbar.mb-10
              recommender.mt-4(v-if="hasContent")
        .card-body.cart-content(:class="{'d-flex flex-column bg-f7 px-0': smDevice}")
            delivery-details.border-top.border-bottom.py-4.mb-6(v-if="smDevice")
            cart-error.mb-6.mb-md-4(
                v-if="!isCheckout && !hideError(error)"
                v-for="(error, index) in errors"
                :key="`cartError-${index}`"
                :icon="getErrorIcon(error)"
                :text="getErrorText(error)"
                :event="getErrorButtonEvent(error)")
                | {{getErrorButtonText(error)}}

            cart-error.mb-6.mb-md-4(
                v-if="!cart.validExpenseRule && cart.calculated.selected_expense_rule"
                icon="fa-solid fa-wallet"
                :text="$t(`cart.allowance.wrong`)")

            .d-flex.flex-column.mb-6.mb-md-0(v-if="hasContent")
                cart-item(
                    v-for="(item, index) in sortedItems"
                    :key="`cartItem-${item.meal}-${index}`"
                    :value="item"
                    :readonly="loading && isCheckout"
                    :inactive="inactiveItems[index]"
                    @input="setCartItem($event, index)"
                    :locale="currency.locale"
                    :currency="currency.currency_code")
            deals.mb-6.mb-md-0(v-if="restaurantIsSet" :restaurant="restaurant" :price="subTotal" :currency="currency")
            cart-payment-details.mb-6(v-if="smDevice" :restaurant="restaurant")
            template(v-if="!hasContent")
                image-wrapper.mt-4(src="/images/empty-cart.svg" background contain)
                p.body-sm.text-center.text-73.mt-6(v-html="$t('cart.empty')")
        .card-footer.shadow-2.shadow-md-none.border-radius-bottom-md-md(v-if="(lgDevice &&  (totalPrice || hasContent)) || smDevice")
            cart-payment-details(v-if="lgDevice &&  (totalPrice || hasContent)" :restaurant="restaurant")
            template(v-if="smDevice")
                transition(name="fade" mode="out-in")
                    template(v-if="restaurantView")
                        go-to-checkout(
                            v-if="hasContent"
                            :checkout-disabled="checkoutDisabled"
                            :go-to-checkout="goToCheckout"
                            :currency="currency"
                            :price="totalPrice"
                            :original-price="originalPrice"
                            :minimum-order="minimumOrder"
                            :sub-total="subTotal"
                        )
                        button.btn.btn-primary.btn-block.btn-lg(v-else @click="hideCart") {{$t('order.see_menu')}}
                    template(v-else)
                        button.btn.btn-primary.btn-block.btn-lg(v-if="hasContent" @click="goToResaurant")
                            | {{$t('order.back_store')}}
                        button.btn.btn-primary.btn-block.btn-lg(@click="hideCart" v-else)
                            | {{$t('base.back_to_restaurants')}}
        modal-inactive-items(ref="inactiveItemsModal" :items="sortedItems" :currency="currency")
</template>
